// Shared backstop for a Telerik popup that was shown but never revealed.
//
// Telerik can return successfully from PopupRef.Show() and still leave the popup parked at
// "left:-10000px; display:none" with no "k-animation-container-shown" class, and its inner
// ".k-child-animation-container" hidden, so the content renders but measures 0 and the control looks
// dead. Two unrelated cases hit this — EBizDatePicker inside a Telerik grid on Firefox, and every
// dropdown popup on most mobile-card-layout loads in Chrome — so the detect-and-reveal half lives
// here and each caller supplies only its own placement.
//
// `position` is called once on reveal and again on scroll/resize while the popup stays open.
// `graceMs` is how long Telerik gets to do it properly first; `giveUpMs` stops the polling.
export function ensureTelerikPopupRevealed(popup, position, { graceMs = 0, giveUpMs = 330 } = {}) {
    if (!popup || typeof position !== "function") return;

    const isRevealed = () =>
        popup.classList.contains("k-animation-container-shown") &&
        popup.style.display !== "none" &&
        popup.style.left !== "-10000px";

    const reveal = () => {
        popup.style.display = "block";
        popup.classList.add("k-animation-container-shown");

        const child = popup.querySelector(".k-child-animation-container");
        if (child) {
            child.style.display = "";
            child.style.opacity = "1";
            child.style.transition = "none";
        }

        position();

        // Keep it aligned while open; self-clean once Telerik's Hide() has closed it.
        const onReflow = () => {
            if (!popup.isConnected || popup.style.display === "none") {
                window.removeEventListener("scroll", onReflow, true);
                window.removeEventListener("resize", onReflow, true);
                return;
            }
            position();
        };
        window.addEventListener("scroll", onReflow, true);
        window.addEventListener("resize", onReflow, true);
    };

    const start = performance.now();
    const check = () => {
        if (isRevealed()) return;                     // Telerik handled it — leave it alone.

        const elapsed = performance.now() - start;
        if (elapsed >= graceMs && popup.querySelector(".k-popup")) {
            reveal();                                 // Content rendered but never revealed — fix it.
            return;
        }
        if (elapsed < giveUpMs) requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
}
